/**
 * v2Api.js — thin client for all /v2/ Cloud Function endpoints.
 *
 * useV2 flag sources (first wins):
 *   1. REACT_APP_USE_V2=false env var (set at build time to force legacy)
 *   2. localStorage key "dev.useV2" = "false" (QA override to disable)
 *   3. Default: true (v2 path)
 *
 * contextId: the BM context for all v2 calls. Default: "bm-2026".
 * Can be overridden via localStorage "dev.contextId".
 */

export const BASE_URL = process.env.REACT_APP_FUNCTIONS_BASE_URL ||
  'https://naviguesser.app';

// Default context ID is set from server config after fetchConfig() resolves (ARC-10).
// 'bm-2026' is a startup fallback only — a year rollover needs only config.json updated.
let _defaultContextId = 'bm-2026';

export function setDefaultContextId(id) {
  if (id) _defaultContextId = id;
}

export function getDefaultContextId() {
  return _defaultContextId;
}

// ── Storage keys ─────────────────────────────────────────────────────────────
// Single source of truth for all localStorage key strings used across the app.

export const STORAGE_KEYS = {
  // Auth
  anonToken: 'anonToken',
  // Sign-in wall engagement counters
  signInWallTotal: 'signInWall.total',
  signInWallPositive: 'signInWall.positive',
  signInWallLastPrompt: 'signInWall.lastPromptVotes',
  // Voting deck state (context-scoped)
  unreviewedEvents: 'unreviewedEvents',
  currentIndex: 'currentIndex',
  latestVotes: 'latestVotes',
  voteCount: 'voteCount',
  lastRenderedButton: 'lastRenderedButton',
  lastVoteTime: 'lastVoteTime',
  selectedGroupId: 'selectedGroupId',
  // Streak page state
  reminderSet: 'reminderSet',
  streakLastInviteAt: 'streak.lastInviteAt',
  // Dev / QA overrides (global)
  devUseV2: 'dev.useV2',
  devContextId: 'dev.contextId',
  devContextName: 'dev.contextName',
  // Cached data (global)
  v2Taxonomy: 'v2.taxonomy',
};

// Keys cleared on trip switch (context-scoped deck and group state).
export const CONTEXT_SCOPED_KEYS = [
  STORAGE_KEYS.unreviewedEvents,
  STORAGE_KEYS.currentIndex,
  STORAGE_KEYS.latestVotes,
  STORAGE_KEYS.voteCount,
  STORAGE_KEYS.lastRenderedButton,
  STORAGE_KEYS.lastVoteTime,
  STORAGE_KEYS.selectedGroupId,
];

// Keys preserved on trip switch (auth identity + global engagement progress).
export const GLOBAL_KEYS = [
  STORAGE_KEYS.anonToken,
  STORAGE_KEYS.signInWallTotal,
  STORAGE_KEYS.signInWallPositive,
  STORAGE_KEYS.signInWallLastPrompt,
  STORAGE_KEYS.v2Taxonomy,
  STORAGE_KEYS.devContextId,
];

// ── flag + context ───────────────────────────────────────────────────────────

function _readUseV2() {
  if (process.env.REACT_APP_USE_V2 === 'false') return false;
  if (process.env.REACT_APP_USE_V2 === 'true') return true;
  // DEV ONLY — localStorage key "dev.useV2" is a developer override with no Admin UI.
  // It is only honoured on localhost to prevent any prod user from accidentally disabling
  // the v2 API path by setting it via browser DevTools (e.g. after reading a blog post).
  // On prod, this key is silently ignored and v2 is always used.
  const isLocalDev = typeof window !== 'undefined' && window.location.hostname === 'localhost';
  if (isLocalDev) {
    const stored = localStorage.getItem(STORAGE_KEYS.devUseV2);
    if (stored === 'false') {
      console.warn('[v2Api] dev.useV2=false — falling back to v1 API path (localhost only)');
      return false;
    }
  }
  return true; // default on
}

let _useV2Flag = _readUseV2();

export function useV2() { return _useV2Flag; }
export function setUseV2(v) {
  _useV2Flag = Boolean(v);
  localStorage.setItem(STORAGE_KEYS.devUseV2, String(_useV2Flag));
}

export function getContextId() {
  return localStorage.getItem(STORAGE_KEYS.devContextId) || _defaultContextId;
}

// ── helpers ──────────────────────────────────────────────────────────────────

async function _getToken(user) {
  return user.getIdToken();
}

async function _get(url, token) {
  const res = await fetch(url, {
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`v2Api GET ${url}: ${res.status} ${res.statusText}`);
  return res.json();
}

async function _post(url, token, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`v2Api POST ${url}: ${res.status} ${res.statusText}`);
  return res.json();
}

async function _delete(url, token) {
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) throw new Error(`v2Api DELETE ${url}: ${res.status} ${res.statusText}`);
  return res.json();
}

// ── art discriminator ────────────────────────────────────────────────────────
// The one rule for telling art from an event, shared by every surface that has
// to make the call. Two things it must NOT be:
//
//   • `availability.always` — that stopped meaning "is art" when ART-SCHED gave
//     art real time windows. It was never reliable in the other direction
//     either: an event with an empty occurrence_set is also always:true.
//   • a substring/prefix match — BM publishes a real event type abbreviated
//     'arts' ("Arts & Crafts", see burning-man-api-docs.md), so 'arts'
//     .startsWith('art') would misfile a craft workshop as an art piece.
//
// Exact array membership only. Mirrors the app's own rule
// (Option.eventType = tags.first, EventLike.isArt = eventType == "art").
export function isArtOption(opt) {
  return (opt?.tags || []).includes('art');
}

// ── Option → legacy card adapter ─────────────────────────────────────────────
// Maps a v2 Option to the shape VotingComponent expects, so it needs no changes.

export function optionToCard(opt) {
  const tags = opt.tags || [];
  const type = isArtOption(opt) ? 'art' : 'event';
  const nonArtTag = tags.find(t => t !== 'art') || null;
  const tagType = tags.length === 0 ? null : (nonArtTag || tags[0]);
  return {
    uid: opt.id,
    title: opt.name || opt.host || opt.address || opt.id,
    type,
    tagType,
    unplaced: opt.unplaced ?? false,
    description: opt.description || '',
    images: (opt.pictures || []).map(url => ({ thumbnail_url: url })),
    location_string: opt.address || '',
    occurrence_set: (opt.availability?.windows || []).map(w => ({
      start_time: w.from,
      end_time: w.until,
    })),
    camp_name: opt.host || '',
    _v2: true,
    _optionId: opt.id,
    _contextId: getContextId(),
  };
}

// ── vote value mapping ────────────────────────────────────────────────────────
// Legacy vote values → v2 vote values

const LEGACY_TO_V2_VOTE = {
  'favorite': 'favorite',
  'up': 'maybe',
  'down': 'reject',
};

export function toV2Vote(legacyVote) {
  if (!(legacyVote in LEGACY_TO_V2_VOTE)) {
    throw new Error(`Unknown vote type: ${legacyVote}`);
  }
  return LEGACY_TO_V2_VOTE[legacyVote];
}

// ── API methods ───────────────────────────────────────────────────────────────

/**
 * Fetch unvoted options for the voting feed.
 * Returns [{uid, title, type, description, images, ...}] (legacy card shape).
 */
export async function fetchFeed(user, { contextId, count = 22, includeAdult = false, firstUid = null } = {}) {
  const ctx = contextId || getContextId();
  const token = await _getToken(user);
  const url = new URL(`${BASE_URL}/v2_feed`);
  url.searchParams.set('context_id', ctx);
  url.searchParams.set('count', count);
  if (includeAdult) url.searchParams.set('include_adult', 'true');
  if (firstUid) url.searchParams.set('first_uid', firstUid);
  const data = await _get(url.toString(), token);
  return (data.options || []).map(optionToCard);
}

/**
 * Fetch user's positive picks (favorite/maybe) for My Picks page.
 * Returns {picks: [{id, title, host, availability, my_vote, my_rating, ...}]}.
 */
export async function fetchPicks(user, { contextId, includeGroups = true } = {}) {
  const ctx = contextId || getContextId();
  const token = await _getToken(user);
  const url = new URL(`${BASE_URL}/v2_picks`);
  url.searchParams.set('context_id', ctx);
  url.searchParams.set('include_groups', includeGroups);
  return _get(url.toString(), token);
}

/**
 * Fetch ratings for options in the context: `{group, mine}` — what your friends voted and what
 * you voted. AIRATE-15a removed the `global` bucket; the crowd aggregate stays on the backend.
 */
export async function fetchRatings(user, { contextId } = {}) {
  const ctx = contextId || getContextId();
  const token = await _getToken(user);
  const url = new URL(`${BASE_URL}/v2_ratings`);
  url.searchParams.set('context_id', ctx);
  return _get(url.toString(), token);
}

/**
 * Post a vote. legacyVote is 'up'|'down'|'favorite'.
 */
export async function postVote(user, { optionId, contextId, legacyVote, clientActionId }) {
  const ctx = contextId || getContextId();
  const token = await _getToken(user);
  return _post(`${BASE_URL}/v2_votes`, token, {
    option_id: optionId,
    context_id: ctx,
    vote: toV2Vote(legacyVote),
    client_action_id: clientActionId || `web-${Date.now()}`,
  });
}

export async function fetchContexts(user = null) {
  let token = null;
  if (user && !user.isAnonymous) {
    token = await _getToken(user);
  }
  return _get(`${BASE_URL}/v2_contexts`, token);
}

export function clearContextScopedCaches() {
  CONTEXT_SCOPED_KEYS.forEach(k => localStorage.removeItem(k));
}

/**
 * Fetch server config (signInWall thresholds, etc.).
 */
export async function fetchConfig() {
  return _get(`${BASE_URL}/config`, null);
}

/**
 * Fetch tag→emoji taxonomy.
 */
export async function fetchTaxonomy() {
  return _get(`${BASE_URL}/v2_taxonomy`, null);
}

/**
 * Migrate anonymous votes to signed-in user (called on sign-in).
 */
export async function updateAnonVotes({ anonToken, signedInToken }) {
  return _post(`${BASE_URL}/update_v2_anonymous_votes`, null, {
    anonymous_token: anonToken,
    signed_in_token: signedInToken,
  });
}

// ── Groups ────────────────────────────────────────────────────────────────────

export async function createGroup(user, { name, contextId } = {}) {
  const ctx = contextId || getContextId();
  const token = await _getToken(user);
  return _post(`${BASE_URL}/v2_groups`, token, { name, context_id: ctx });
}

export async function listGroups(user) {
  const token = await _getToken(user);
  return _get(`${BASE_URL}/v2_groups`, token);
}

export async function joinGroup(user, { linkKey }) {
  const token = await _getToken(user);
  return _post(`${BASE_URL}/v2_groups/join`, token, { link_key: linkKey });
}

export async function leaveGroup(user, { groupId }) {
  const token = await _getToken(user);
  return _delete(`${BASE_URL}/v2_groups/${groupId}`, token);
}

// ── Admin helpers ─────────────────────────────────────────────────────────────

/**
 * Trigger a BM data import (admin only).
 * Returns response JSON, throws on HTTP error.
 */
export async function triggerBmImport(user) {
  const token = await _getToken(user);
  const res = await fetch(`${BASE_URL}/bm_import?fetch=true`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: '',
  });
  if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
  return res.json();
}

// ── Option lookup ─────────────────────────────────────────────────────────────

/**
 * Fetch a single Option by its Firestore document ID.
 * Uses the v2_options direct-lookup path (?option_id=).
 * Returns the option dict, or null if not found or on error.
 */
export async function fetchOptionById(optionId, contextId) {
  const res = await fetch(
    `${BASE_URL}/v2_options?context_id=${encodeURIComponent(contextId)}&option_id=${encodeURIComponent(optionId)}`
  );
  if (!res.ok) return null;
  const data = await res.json();
  const items = Array.isArray(data.options) ? data.options : [];
  return items[0] || null;
}

// ── OTP auth helpers ──────────────────────────────────────────────────────────

/**
 * Request an OTP code to be sent to `email`.
 * Returns {ok: boolean, data: any, status: number}.
 */
export async function requestOtpCode(email) {
  const res = await fetch(`${BASE_URL}/otp_code/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data, status: res.status };
}

/**
 * Verify an OTP code.
 * Returns {ok: boolean, data: any, status: number}.
 */
export async function verifyOtpCode({ email, code, subscribe, consentAccepted }) {
  const res = await fetch(`${BASE_URL}/otp_code/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, subscribe, consentAccepted }),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data, status: res.status };
}
